import { useCart, getCartItemKey } from "../../Contexts/CartContext";
import { useTenant } from "../../Contexts/TenantContext";
import { formatPriceBRL } from "../../Utils/DataFormatters";
import { CartItem } from "../../Types/CartItemType";
import { FaTrash } from "react-icons/fa";
import QuantitySelector from "../QuantitySelector/QuantitySelector";

type CartProductItemProps = {
  item: CartItem;
};

const CartProductItem = ({ item }: CartProductItemProps) => {
  const { removeFromCart, increaseQuantity, decreaseQuantity } = useCart();
  const { tenantPreference } = useTenant();
  const hideProductCode = tenantPreference?.autoGenerateProductCode === true;
  const key = getCartItemKey(item);

  return (
    <div className="border-b p-3 sm:p-4">
      <div className="flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-2">
        {item.image ? (
          <img
            src={item.image}
            alt={item.name}
            className="h-12 w-12 object-contain flex-shrink-0"
          />
        ) : (
          <div className="h-12 w-12 flex items-center justify-center bg-gray-100 text-gray-400 text-[10px] sm:text-xs rounded flex-shrink-0 text-center leading-tight">
            {"Sem imagem"}
          </div>
        )}
        {/* Below sm the name fills its row, with the quantity and price on a row of their own */}
        <div className="w-[calc(100%-3.75rem)] sm:w-auto sm:flex-1 min-w-0">
          {/* Two lines and one line are reserved so every item has the same height */}
          <p className="text-xs sm:text-sm line-clamp-2 [overflow-wrap:anywhere] min-h-8 sm:min-h-10" title={item.name}>{item.name}</p>
          <div className="mt-1 flex items-center gap-1.5 min-h-6 min-w-0">
            {!hideProductCode && item.code && <p className="text-xs truncate">{"CÓD."} {item.code}</p>}
            {item.size && (
              <span className="inline-flex items-center flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] sm:text-[11px] font-semibold bg-gray-100 text-gray-700 border border-gray-300">
                {item.size}
              </span>
            )}
            {item.isMadeToOrder && item.stockQuantity <= 0 && (
              <span className="inline-flex items-center flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] sm:text-[11px] bg-primary-50 text-primary-700 border border-primary-200">
                {"Encomenda"}
              </span>
            )}
          </div>
        </div>
        <div className="flex sm:flex-col items-center gap-2 sm:gap-0 min-[360px]:ml-[3.75rem] sm:ml-0 flex-shrink-0 sm:w-24 select-none">
          <p className="hidden sm:block text-sm">{"Quant."}</p>
          <div className="min-w-20 sm:min-w-0">
            <QuantitySelector
              value={item.quantity}
              min={1}
              max={item.isMadeToOrder ? undefined : item.stockQuantity}
              onChange={(newQty) => {
                if (newQty > item.quantity) {
                  increaseQuantity(key);
                }
                else if (newQty < item.quantity) {
                  decreaseQuantity(key);
                }
              }}
            />
          </div>
          <button
            className="flex items-center gap-1 p-1 sm:p-0 text-sm sm:text-xs sm:mt-5 text-primary-600 font-bold hover:text-primary-700 hover:underline"
            onClick={() => removeFromCart(key)}
            aria-label={`Remover ${item.name}`}
          >
            <FaTrash />
            <span className="hidden sm:inline">{"REMOVER"}</span>
          </button>
        </div>
        <div className="ml-auto sm:ml-0 text-sm sm:text-base text-primary-600 font-semibold flex-shrink-0 text-right whitespace-nowrap sm:min-w-28">
          {formatPriceBRL(item.price * item.quantity)}
        </div>
      </div>
    </div>
  );
};

export default CartProductItem;
